import React, { useEffect, memo, useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import {
  InlineMessage,
  InlineMessageProps,
  INLINE_MESSAGE_VARIANTS,
} from 'transfix-ui/components/InlineMessage'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, BoxProps as MuiBoxProps } from 'transfix-ui/core'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useValidateTruckNumber } from 'apps/fleet-planner/types'

type TStatus = 'valid' | 'invalid' | 'error'

const ALERTS: Record<TStatus, InlineMessageProps> = {
  valid: {
    type: INLINE_MESSAGE_VARIANTS.INFO,
    message: 'This truck is set up for Live Tracking',
  },
  invalid: {
    type: INLINE_MESSAGE_VARIANTS.INFO,
    title: 'This truck isn’t set up for Live Tracking.',
    message: (
      <>
        Please enter a truck number listed in your Project44 account. To add a new truck, please
        email us at <a href='mailto:support@transfix.io'>support@transfix.io</a> and we will add it
        for you.
      </>
    ),
  },
  error: {
    type: INLINE_MESSAGE_VARIANTS.ALERT,
    message: 'There was an error when fetching this information.',
  },
}

/**
 * Returns props based on TStatus keyName argument
 */
const getAlertProps = (
  keyName: keyof typeof ALERTS | undefined
): InlineMessageProps | undefined => {
  if (!keyName || !ALERTS[keyName]) return undefined
  return ALERTS[keyName]
}

interface IEnableLiveTrackingButtonProps {
  shipmentId: string | undefined
  truckNumber: string | undefined
  BoxProps?: MuiBoxProps
}

/**
 * Checks is a truck number is valid in project44
 */
export const EnableLiveTrackingButton = memo<IEnableLiveTrackingButtonProps>(
  ({ truckNumber, shipmentId, BoxProps = {} }) => {
    const { trackEvent } = useFMSAnalytics()
    const [status, setStatus] = useState<TStatus | undefined>()
    const { loading, mutate: validateTruckNumber } = useValidateTruckNumber({
      id: Number(shipmentId),
    })

    /**
     * When there's a new shipment or truckNumber, reset this value
     */
    useEffect(() => {
      setStatus(undefined)
    }, [shipmentId, truckNumber])

    /**
     * Triggers API method to check if truck is valid
     */
    const clickHandler = async () => {
      trackEvent('Tracking', 'CLICKED_ENABLE_LIVE_TRACKING', { truckNumber })

      // reset status on new request
      setStatus(undefined)

      try {
        const response = await validateTruckNumber({
          truckNumber,
        })
        const { valid } = response || {}

        if (valid) {
          setStatus('valid')
        } else {
          setStatus('invalid')
        }
      } catch {
        setStatus('error')
        trackEvent('Tracking', 'ENABLE_LIVE_TRACKING_ERROR', { truckNumber })
      }
    }

    const alertProps = getAlertProps(status)

    return (
      <Box data-test='enable-live-tracking' {...BoxProps}>
        <Box>
          <Button color='primary' onClick={clickHandler} disabled={loading}>
            Click to Enable Live Tracking
          </Button>
        </Box>
        {loading && (
          <Box p={2} display='flex' alignItems='center' justifyContent='center'>
            <Spinner position='relative' />
          </Box>
        )}
        {alertProps && !loading && (
          <Box mt={2} data-test='enable-live-tracking-message'>
            <InlineMessage {...alertProps} />
          </Box>
        )}
      </Box>
    )
  }
)
